
import { useState, useEffect } from 'react';
import { bookmarkService } from '@/services/article/bookmarkService';
import { useAuth } from '@/hooks/useAuth';
import { toast } from '@/components/ui/use-toast';

export interface SavedArticle {
  id: string;
  savedAt: string;
  article: {
    id: string;
    title: string;
    lead?: string;
    image?: string;
    publishedAt?: string;
    readTime?: number;
    author: {
      id?: string;
      name: string;
      avatar?: string;
    }
  }
}

export const useSavedArticles = () => {
  const [savedArticles, setSavedArticles] = useState<SavedArticle[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { user } = useAuth();

  const fetchSavedArticles = async () => {
    if (!user) {
      setSavedArticles([]);
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const { data, error } = await bookmarkService.getUserBookmarks();
      
      if (error) throw error;
      
      setSavedArticles(data);
    } catch (err) {
      console.error('Error fetching saved articles:', err);
      setError(err instanceof Error ? err : new Error('Failed to load saved articles'));
      toast({
        title: 'Error loading saved articles',
        description: 'There was a problem loading your saved articles.',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const unsaveArticle = async (articleId: string) => {
    try {
      const { success } = await bookmarkService.unsaveArticle(articleId);
      
      if (success) {
        setSavedArticles(savedArticles.filter(item => item.article.id !== articleId));
        toast({
          title: 'Article removed from bookmarks',
          description: 'The article has been removed from your bookmarks.'
        });
      }
    } catch (err) {
      console.error('Error removing bookmark:', err);
      toast({
        title: 'Error removing bookmark',
        description: 'There was a problem removing this bookmark.',
        variant: 'destructive'
      });
    }
  };

  useEffect(() => {
    if (user) {
      fetchSavedArticles();
    } else {
      setSavedArticles([]);
      setIsLoading(false);
    }
  }, [user]);

  return {
    savedArticles,
    isLoading,
    error,
    refreshSavedArticles: fetchSavedArticles,
    unsaveArticle
  };
};
